import { useState } from 'react'

import { Button } from '@/components/ui/button'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { cn } from '@/lib/utils'

interface ConfirmRemoveUserStepProps {
	open: boolean
	userName: string
	hasDirectReports: boolean
	isLoading: boolean
	onConfirm: (reason: string) => void
	onBack: () => void
	onCancel: () => void
}

export function ConfirmRemoveUserStep({
	open,
	userName,
	hasDirectReports,
	isLoading,
	onConfirm,
	onBack,
	onCancel,
}: ConfirmRemoveUserStepProps) {
	const [reason, setReason] = useState('')
	const [confirmName, setConfirmName] = useState('')

	const isNameMatch = confirmName.trim() === userName.trim()
	const hasReason = reason.length > 0

	return (
		<Dialog open={open} onOpenChange={(o) => { if (!o) onCancel() }}>
			<DialogContent className="sm:max-w-[500px]">
				<DialogHeader>
					<DialogTitle>Remove {userName}</DialogTitle>
					<DialogDescription>
						{hasDirectReports
							? `All direct reports have been reassigned. Please confirm the removal.`
							: `Are you sure you want to remove ${userName} from the organization?`}
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-4 py-4">
					{/* Reason selection */}
					<div className="space-y-2">
						<Label>Reason for removal</Label>
						<RadioGroup
							value={reason}
							onValueChange={setReason}
							className="space-y-2 pt-1"
						>
							<div className="flex items-center gap-2">
								<RadioGroupItem value="terminated" id="terminated" />
								<Label
									htmlFor="terminated"
									className="font-normal cursor-pointer"
								>
									{userName} was terminated
								</Label>
							</div>
							<div className="flex items-center gap-2">
								<RadioGroupItem value="resigned" id="resigned" />
								<Label
									htmlFor="resigned"
									className="font-normal cursor-pointer"
								>
									{userName} resigned
								</Label>
							</div>
							<div className="flex items-center gap-2">
								<RadioGroupItem value="transferred" id="transferred" />
								<Label
									htmlFor="transferred"
									className="font-normal cursor-pointer"
								>
									{userName} transferred to another department
								</Label>
							</div>
							<div className="flex items-center gap-2">
								<RadioGroupItem value="other" id="other" />
								<Label htmlFor="other" className="font-normal cursor-pointer">
									Other reason
								</Label>
							</div>
						</RadioGroup>
					</div>

					{/* Name confirmation */}
					<div className="space-y-2">
						<Label htmlFor="confirmName">
							Type <span className="font-semibold">"{userName}"</span> to
							confirm
						</Label>
						<Input
							id="confirmName"
							placeholder="Enter user's full name"
							value={confirmName}
							onChange={(e) => setConfirmName(e.target.value)}
							className={cn(
								!isNameMatch && confirmName ? 'border-destructive' : '',
							)}
						/>
						{!isNameMatch && confirmName ? (
							<p className="text-sm text-destructive">Name does not match</p>
						) : null}
					</div>

					<p className="text-sm text-muted-foreground">
						This action will remove the user from the organization. This
						cannot be undone.
					</p>
				</div>

				<DialogFooter>
					{hasDirectReports && (
						<Button
							type="button"
							variant="ghost"
							onClick={onBack}
							disabled={isLoading}
						>
							Back
						</Button>
					)}
					<Button
						type="button"
						variant="outline"
						onClick={onCancel}
						disabled={isLoading}
					>
						Cancel
					</Button>
					<Button
						type="button"
						variant="destructive"
						onClick={() => onConfirm(reason)}
						disabled={isLoading || !isNameMatch || !hasReason}
					>
						{isLoading ? 'Removing...' : 'Remove User'}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}
